import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { PriorityFlowTier } from "../../enums/scan";
import { JOURNEY_ROLE, PRIORITY_FLOW_TIER } from "../../enums/scan";
import type {
  CandidateJourney,
  JourneyPage,
  PriorityContentFlow,
  PriorityFlowPreview,
} from "./journeyTypes";
import {
  brokenOrDeadEndCount,
  buildPriorityFlowConcerns,
  criticalHighConcernCount,
  priorityPageCount,
  priorityReasonForConcerns,
  priorityTierForConcerns,
} from "./priorityFlowConcerns";
import {
  buildMergedFlowGroups,
  type PriorityFlowGroup,
} from "./priorityFlowGrouping";
import {
  contentFlowLabel,
  flowCompatibilitySortKey,
} from "./priorityFlowLabels";
import { retainFlowRoutes } from "./priorityFlowRoutes";
import { clusterContextForPages } from "./scanModel";

const PRIORITY_FLOW_LIMIT = 3;

type RankedFlow = {
  flow: PriorityContentFlow;
  compatibilitySortKey: string;
};

export function buildPriorityFlowPreview(
  data: OverviewResponse,
  paths: CandidateJourney[],
): PriorityFlowPreview {
  const clusterContext = clusterContextForPages(data.pages);
  const all = buildMergedFlowGroups(data, paths)
    .map((group) => ({
      flow: buildContentFlow(data, group, clusterContext),
      compatibilitySortKey: flowCompatibilitySortKey(
        group.paths,
        data.pages,
        clusterContext,
      ),
    }))
    .sort(compareFlows)
    .map(({ flow }) => flow);
  const supported = all.filter(
    (flow) => flow.priorityTier !== PRIORITY_FLOW_TIER.CONTEXT,
  );
  const topLevel =
    supported.length > 0
      ? supported.slice(0, PRIORITY_FLOW_LIMIT)
      : all[0]
        ? [{ ...all[0], neutralRepresentative: true }]
        : [];
  const topIds = new Set(topLevel.map((flow) => flow.id));
  return {
    topLevel,
    other: all.filter((flow) => !topIds.has(flow.id)),
    all,
  };
}

export function flowForPath(
  preview: PriorityFlowPreview,
  pathId: string | null | undefined,
): PriorityContentFlow | null {
  if (!pathId) return preview.topLevel[0] ?? null;
  return (
    preview.all.find((flow) =>
      flow.candidatePaths.some((path) => path.id === pathId),
    ) ??
    preview.topLevel[0] ??
    null
  );
}

function buildContentFlow(
  data: OverviewResponse,
  group: PriorityFlowGroup,
  clusterContext: ReturnType<typeof clusterContextForPages>,
): PriorityContentFlow {
  const candidatePaths = [...group.paths].sort(
    (left, right) => left.rank - right.rank || left.id.localeCompare(right.id),
  );
  const concerns = buildPriorityFlowConcerns(data, candidatePaths);
  const affectedPriorityPages = priorityPageCount(concerns);
  const representativePath = strongestPath(data, candidatePaths);
  return {
    id: `priority-${group.keys.join("+")}`,
    label: contentFlowLabel(candidatePaths, data.pages, clusterContext),
    candidatePaths,
    paths: retainFlowRoutes(candidatePaths, representativePath),
    representativePath,
    uniquePages: uniquePages(candidatePaths),
    priorityTier: priorityTierForConcerns(concerns, affectedPriorityPages),
    priorityReason: priorityReasonForConcerns(concerns, affectedPriorityPages),
    priorityConcernCount: concerns.length,
    priorityPageCount: affectedPriorityPages,
    concerns,
    neutralRepresentative: false,
  };
}

function strongestPath(
  data: OverviewResponse,
  paths: CandidateJourney[],
): CandidateJourney {
  const concernCounts = new Map(
    paths.map((path) => [
      path.id,
      pathConcernStats(buildPriorityFlowConcerns(data, [path])),
    ]),
  );
  return [...paths].sort((left, right) => {
    const leftConcerns = concernCounts.get(left.id) ?? {
      criticalHigh: 0,
      brokenOrDead: 0,
    };
    const rightConcerns = concernCounts.get(right.id) ?? {
      criticalHigh: 0,
      brokenOrDead: 0,
    };
    return (
      rightConcerns.criticalHigh - leftConcerns.criticalHigh ||
      rightConcerns.brokenOrDead - leftConcerns.brokenOrDead ||
      Number(hasMeaningfulDestination(right)) -
        Number(hasMeaningfulDestination(left)) ||
      right.assessedPages - left.assessedPages ||
      right.steps.length - left.steps.length ||
      left.rank - right.rank ||
      left.id.localeCompare(right.id)
    );
  })[0] as CandidateJourney;
}

function compareFlows(leftRanked: RankedFlow, rightRanked: RankedFlow): number {
  const left = leftRanked.flow;
  const right = rightRanked.flow;
  return (
    tierOrder(left.priorityTier) - tierOrder(right.priorityTier) ||
    criticalHighConcernCount(right.concerns) -
      criticalHighConcernCount(left.concerns) ||
    right.priorityPageCount - left.priorityPageCount ||
    brokenOrDeadEndCount(right.concerns) -
      brokenOrDeadEndCount(left.concerns) ||
    Number(hasMeaningfulDestination(right.representativePath)) -
      Number(hasMeaningfulDestination(left.representativePath)) ||
    right.uniquePages.length - left.uniquePages.length ||
    left.representativePath.rank - right.representativePath.rank ||
    leftRanked.compatibilitySortKey.localeCompare(
      rightRanked.compatibilitySortKey,
    )
  );
}

function pathConcernStats(concerns: PriorityContentFlow["concerns"]): {
  criticalHigh: number;
  brokenOrDead: number;
} {
  return {
    criticalHigh: criticalHighConcernCount(concerns),
    brokenOrDead: brokenOrDeadEndCount(concerns),
  };
}

function uniquePages(paths: CandidateJourney[]): JourneyPage[] {
  return [
    ...new Map(
      paths.flatMap((path) =>
        path.steps.map((step) => [step.page.id, step.page] as const),
      ),
    ).values(),
  ];
}

function hasMeaningfulDestination(path: CandidateJourney): boolean {
  const role = path.steps.at(-1)?.role;
  return role === JOURNEY_ROLE.ACTION || role === JOURNEY_ROLE.DESTINATION;
}

function tierOrder(tier: PriorityFlowTier): number {
  return { critical: 0, high: 1, review: 2, context: 3 }[tier];
}
